// Salinity unit conversion. Storage is always ppt (per the
// 2026-04-26-sensei-score-implementation decision); the S.G. input
// path on Home converts on save so reefers who think in specific
// gravity from a refractometer can type 1.026 directly.
//
// The slope is anchored to the pair every reefer knows: 1.026 S.G.
// ↔ 35.0 ppt (the hobby-chart convention printed on refractometers
// and salt buckets). An earlier constant (1283.5) mapped 1.026 to
// 33.4 ppt — outside the default 34.7–35.3 acceptable band — so a
// keeper typing the canonical target got a false LOW alarm. Linear
// is fine here: hobby refractometers resolve ~0.001 S.G. (≈1.3 ppt),
// far coarser than the curvature of the real seawater equation of
// state across the reef range.

const PPT_PER_SG = 35 / 0.026; // ≈1346.15 — anchors 1.026 ↔ 35.0
const SG_BASE = 1.0;

export function sgToPpt(sg) {
  if (sg == null || sg === '') return null;
  const n = Number(sg);
  if (!Number.isFinite(n)) return null;
  return Math.round((n - SG_BASE) * PPT_PER_SG * 10) / 10;
}

// `decimals` defaults to the refractometer-native 3 places; band
// displays pass 4 so a ±0.3 ppt acceptable range doesn't collapse
// into "1.026–1.026".
export function pptToSg(ppt, decimals = 3) {
  if (ppt == null || ppt === '') return null;
  const n = Number(ppt);
  if (!Number.isFinite(n)) return null;
  const f = 10 ** decimals;
  return Math.round((SG_BASE + n / PPT_PER_SG) * f) / f;
}

// Reef-tank guardrail. Inputs outside this range are almost certainly
// a typo (1.26 instead of 1.026, 350 instead of 35). Used by the
// quick-add to reject before saving.
export const SG_REEF_MIN = 1.005;
export const SG_REEF_MAX = 1.04;
export const PPT_REEF_MIN = 5;
export const PPT_REEF_MAX = 55;

export function isPlausibleSg(sg) {
  const n = Number(sg);
  return Number.isFinite(n) && n >= SG_REEF_MIN && n <= SG_REEF_MAX;
}
export function isPlausiblePpt(ppt) {
  const n = Number(ppt);
  return Number.isFinite(n) && n >= PPT_REEF_MIN && n <= PPT_REEF_MAX;
}

// ─── Display-unit preference (ppt | psu | sg) ─────────────
// Storage is ALWAYS ppt; this layer converts on the way in and out.
// PSU is numerically identical to ppt for hobby purposes (35 PSU =
// 35 ppt) — different label, same number. S.G. uses the anchored
// slope above. The preference lives in localStorage under the same
// key the old two-state (ppt|sg) input toggle used, so existing
// users' choices carry over.
export const SALINITY_UNIT_KEY = 'aqs.salinityInputUnit';
export const SALINITY_UNITS = [
  { k: 'ppt', label: 'ppt' },
  { k: 'psu', label: 'PSU' },
  { k: 'sg', label: 'S.G.' },
];

export function readSalinityUnit() {
  try {
    const v = localStorage.getItem(SALINITY_UNIT_KEY);
    return v === 'sg' || v === 'psu' ? v : 'ppt';
  } catch {
    return 'ppt';
  }
}
export function writeSalinityUnit(u) {
  try {
    localStorage.setItem(SALINITY_UNIT_KEY, u);
  } catch {
    /* private mode — non-fatal */
  }
}

export function salinityUnitLabel(unit) {
  return unit === 'sg' ? 'S.G.' : unit === 'psu' ? 'PSU' : 'ppt';
}

// Stored ppt → display number in the chosen unit.
export function formatSalinity(ppt, unit, decimals) {
  if (ppt == null || !Number.isFinite(Number(ppt))) return null;
  if (unit === 'sg') return pptToSg(ppt, decimals ?? 3);
  return Math.round(Number(ppt) * 10) / 10;
}

// Typed/photographed number in the chosen unit → stored ppt. Also
// auto-detects scale mismatches: a "ppt" reading under 2 can only be
// an S.G. value (35 ppt vs 1.026), and an "S.G." reading over 2 can
// only be ppt/PSU — refractometers carry both scales and Vision (or
// the user) may read either column regardless of the preference.
export function salinityToPpt(value, _unit) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  // The scale itself is unambiguous: S.G. values live below 2,
  // ppt/PSU values above — so detection beats the selected unit.
  // (35 typed while S.G. is selected is obviously ppt; 1.026 typed
  // while ppt is selected is obviously S.G.)
  if (n > 0.9 && n < 2) return sgToPpt(n);
  return Math.round(n * 10) / 10;
}

export function isPlausibleSalinity(value, unit) {
  const n = Number(value);
  if (!Number.isFinite(n)) return false;
  if (unit === 'sg') return isPlausibleSg(n);
  // ppt / PSU share the band; also accept an S.G.-scale number the
  // converter will auto-detect.
  return isPlausiblePpt(n) || isPlausibleSg(n);
}

// A stored ppt range in the keeper's scale, with its label. On S.G. the
// third decimal is what a refractometer reads; a band narrower than that
// (34.7-35.3 ppt) collapsed to "1.026–1.026" there, so it keeps a fourth.
// `decimals` (S.G. only) prints the band at the precision chosen for the
// value beside it (sgDecimals), so the two always read alike.
export function formatSalinityRange(lo, hi, unit, decimals) {
  const label = salinityUnitLabel(unit);
  const d = unit === 'sg' ? (decimals ?? sgDecimals([lo, hi])) : undefined;
  return `${formatSalinity(lo, unit, d)}–${formatSalinity(hi, unit, d)} ${label}`;
}

// How many S.G. decimals to show a value and its band with: three, what a
// refractometer reads; four when the band collapses at three (34.7-35.3
// ppt is 1.026-1.026); and one more when an off-band value would print
// equal to the band edge. "Salinity is high: 1.026, your range is
// 1.0258-1.0262" put a HIGH beside a number that read inside the range.
export function sgDecimals(band, value) {
  let d = 3;
  const [lo, hi] = Array.isArray(band) && band.length === 2 ? band.map(Number) : [];
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return d;
  if (pptToSg(lo, 3) === pptToSg(hi, 3)) d = 4;
  const v = Number(value);
  if (value == null || value === '' || !Number.isFinite(v)) return d;
  while (
    d < 5 &&
    ((v > hi && pptToSg(v, d) <= pptToSg(hi, d)) || (v < lo && pptToSg(v, d) >= pptToSg(lo, d)))
  ) {
    d += 1;
  }
  return d;
}

// Ends a sentence after text that may already end in a period ("S.G."),
// so copy never reads "1.026 S.G.." .
export function endSentence(text) {
  const t = String(text ?? '');
  return /[.!?]$/.test(t) ? t : `${t}.`;
}
